import { portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * The VoiceAgent page's **Results** tab — a survey VoiceAgent's answers (2026-10-03, design §16.7.2 AS
 * BUILT 2026-10-03).
 *
 * Shown only for a survey VoiceAgent (`agent-tabs.ts`). Its body is the Survey results screen's own
 * (`SurveyResultBody`), over every conversation of the survey — inbound, every campaign's, and the test
 * ones when asked — so the prototype's `Survey results` design is the one drawn here too and the two
 * can never disagree. The chip row picks a campaign; the Survey results screen is the same page with
 * one campaign fixed.
 *
 * Above the body: the tab's heading and Download, which exports exactly this page (its filter) through
 * `requestSurveyDownload` — audited, owner and admin only (the API refuses anyone else and the dialog
 * prints the refusal). AMENDED 2026-11-03 (owner): every role that reads the results downloads them, the file's
 * content following the role (the dialog says what the role gets); a role the API refuses sees Download disabled,
 * with the reason as its title.
 */
import { useQuery } from "@tanstack/react-query";
import { Button, Icon } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import { SurveyDownloadDialog } from "../../campaigns/survey-download-dialog.js";
import { SurveyResultBody, type SurveyResultSearchView } from "../../campaigns/survey-result-body.js";
import { countText } from "../../campaigns/survey-result-copy.js";
import { GLYPH } from "../../../shell/glyph.js";
import { useGateway } from "../../../api/provider.js";
import { useMemberCan } from "../../../shell/member-capability.js";
import { surveyResultQuery } from "../../../api/query.js";

export interface ResultsTabProps extends SurveyResultSearchView {
  readonly voiceAgentId: string;
  readonly onSearch: (next: Partial<SurveyResultSearchView>) => void;
}

export function ResultsTab(props: ResultsTabProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const [downloadOpen, setDownloadOpen] = useState(false);
  // 2026-11-03: every role that reads the results may download them (the API's roles; a Billing member may not).
  const member = useMemberCan();
  const result = useQuery(surveyResultQuery(gateway, props.voiceAgentId, props.filter));

  return (
    <>
      <div className="c-va-titlerow c-va-titlerow-loose">
        <div>
          <p className="c-va-summary">
            {result.data === undefined
              ? portalText("operations", "adding_up_the_answers_d0207578")
              : portalText("operations", "value_completed_of_value_conversations_c8c0ddca", { v0: String(countText(result.data.completedCount)), v1: String(countText(result.data.conversationCount)) })}
          </p>
          <p className="c-va-subtitle">{portalText("operations", "survey_results_5774f95e")}</p>
        </div>
        <span className="c-row">
          <Button
            variant="secondary"
            disabled={result.data === undefined || !member.can("survey_result.export")}
            title={member.whyNot("survey_result.export") ?? undefined}
            onClick={() => {
              setDownloadOpen(true);
            }}
          >
            <Icon glyph={GLYPH.downloadSimple} size="md" />
            {portalText("operations", "download_a479c9c3")}</Button>
        </span>
      </div>

      <SurveyResultBody
        voiceAgentId={props.voiceAgentId}
        campaignScope={null}
        filter={props.filter}
        view={props.view}
        outcome={props.outcome}
        onSearch={props.onSearch}
      />

      <SurveyDownloadDialog
        open={downloadOpen}
        voiceAgentId={props.voiceAgentId}
        filter={props.filter}
        conversationCount={result.data?.conversationCount ?? 0}
        onClose={() => {
          setDownloadOpen(false);
        }}
      />
    </>
  );
}
